import { Box, Paper, Tooltip, Typography } from "@mui/material";
import CopyToYearIcon from "../../../../../assets/copy to year 2.svg";
import CopyToMonthIcon from "../../../../../assets/copy to month 2.svg";
import CopyToQuarterIcon from "../../../../../assets/copy to quarter 2.svg";
import {
  ComponentConfig,
  getCurrentMetricKeyGeneric,
} from "./helpers/Forecastcopyhelper";

interface InfoCardProps {
  isEditing: boolean;
  editableStates: Record<string, string | null>;
  config: ComponentConfig;
  onCopyToNext: (type: "month" | "quarter" | "year") => void;

  // NEW: Simple props for value source & impact
  selectedFieldName?: string | null;
  fieldType?: "User input" | "Parameter" | "Formula" | null;
  valueSourceText?: string | null;
  impactText?: string | null;
}

const InfoCard = ({
  isEditing,
  editableStates,
  config,
  onCopyToNext,
  selectedFieldName,
  fieldType,
  valueSourceText,
  impactText,
}: InfoCardProps) => {
  const getCurrentEditingInfo = () => {
    return getCurrentMetricKeyGeneric(editableStates, config);
  };

  const currentEditing = getCurrentEditingInfo();
  const selectedMonth = currentEditing?.month || null;

  const getMonthIndex = (monthName: string): number => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months.indexOf(monthName);
  };

  const getButtonStates = () => {
    if (!isEditing || !selectedMonth) {
      return { monthEnabled: false, quarterEnabled: false, yearEnabled: false };
    }

    const monthIndex = getMonthIndex(selectedMonth);
    if (monthIndex === -1) {
      return { monthEnabled: false, quarterEnabled: false, yearEnabled: false };
    }

    const monthEnabled = monthIndex < 11;
    const quarterEndMonths = [2, 5, 8, 11];
    const quarterEnabled = !quarterEndMonths.includes(monthIndex);
    const yearEnabled = monthIndex < 11;

    return { monthEnabled, quarterEnabled, yearEnabled };
  };

  const buttonStates = getButtonStates();

  const handleCopy = (type: "month" | "quarter" | "year") => {
    if (!currentEditing) {
      console.error("ERROR: No field is currently being edited");
      return;
    }
    onCopyToNext(type);
  };

  const renderFormula = (formula: string) => {
    // Split by the first colon to separate label from formula
    const colonIndex = formula.indexOf(":");

    if (colonIndex === -1) {
      // No colon found, render entire text in secondary color
      return (
        <Typography
          component="div"
          variant="body2"
          sx={{
            color: "#7B1FA2",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: impactText ? "4px" : 0,
          }}
        >
          {formula}
        </Typography>
      );
    }

    const label = formula.substring(0, colonIndex + 1); // Include the colon
    const formulaPart = formula.substring(colonIndex + 1).trim();

    return (
      <Typography
        component="div"
        variant="body2"
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          marginBottom: impactText ? "4px" : 0,
        }}
      >
        <span style={{ color: "rgba(0, 0, 0, 0.87)" }}>{label}</span>{" "}
        <span style={{ color: "#7B1FA2" }}>{formulaPart}</span>
      </Typography>
    );
  };

  const renderImpactText = (text: string) => {
    // Split by colon to find the part after "you will affect:"
    const colonIndex = text.indexOf(":");

    if (colonIndex === -1) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "rgba(0, 0, 0, 0.6)",
            fontSize: "14px",
            lineHeight: "20px",
          }}
        >
          {text}
        </Typography>
      );
    }

    const beforeColon = text.substring(0, colonIndex + 1);
    const afterColon = text.substring(colonIndex + 1);

    // Split the part after colon by commas to identify field names
    const fieldNames = afterColon.split(",").map((name) => name.trim());

    return (
      <Typography
        component="div"
        variant="body2"
        sx={{
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: "14px",
          lineHeight: "20px",
        }}
      >
        {beforeColon}
        {fieldNames.map((field, index) => (
          <span key={index}>
            {index > 0 && ","}{" "}
            <strong style={{ fontWeight: 600 }}>{field}</strong>
          </span>
        ))}
      </Typography>
    );
  };

  // NEW: Show value source section if we have a selected field
  const showValueSource = selectedFieldName && (valueSourceText || impactText);

  if (!showValueSource) {
    return (
      <Paper
        elevation={0}
        sx={{
          padding: "16px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          maxWidth: "100%",
          gap: 4,
        }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <Typography
            sx={{
              position: "absolute",
              top: "-8px",
              left: "12px",
              backgroundColor: "#fff",
              px: 0.5,
              fontSize: "12px",
              fontWeight: 500,
              color: "rgba(0,0,0,0.6)",
            }}
          >
            Value Source & Impact
          </Typography>

          <Box
            sx={{
              border: "1px solid #000000",
              borderRadius: "4px",
              padding: "16px",
              fontSize: "14px",
              // lineHeight: "20px",
              backgroundColor: "#fff",
              color: "rgba(0,0,0,0.6)",
            }}
          >
            <div>Here you will see how this value was calculated.</div>

            <div style={{ marginTop: "4px" }}>
              Which fields will be affected by editing it.
            </div>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {[
            { icon: CopyToMonthIcon, title: "Copy to next month" },
            { icon: CopyToQuarterIcon, title: "Copy to end of quarter" },
            { icon: CopyToYearIcon, title: "Copy to end of year" },
          ].map((item, index) => (
            <Tooltip key={index} title={item.title} arrow placement="top">
              <Box
                sx={{
                  padding: "4px",
                  borderRadius: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "not-allowed",
                  opacity: 0.35,
                  filter: "grayscale(100%)",
                }}
              >
                <img
                  src={item.icon}
                  alt={item.title}
                  style={{
                    width: "32px",
                    height: "32px",
                    display: "block",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        padding: "16px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: showValueSource ? "space-between" : "flex-end",
        maxWidth: "100%",
        gap: 4,
      }}
    >
      {/* NEW: Left Section - Value Source & Impact */}
      {showValueSource && (
        <Box sx={{ position: "relative", width: "100%" }}>
          <Typography
            sx={{
              position: "absolute",
              top: "-8px",
              left: "12px",
              backgroundColor: "#fff",
              px: 0.5,
              fontSize: "12px",
              fontWeight: 500,
              color: "rgba(0,0,0,0.6)",
            }}
          >
            Value Source & Impact
          </Typography>
          <Box
            sx={{
              border: "1px solid #000000",
              borderRadius: "4px",
              padding: "12px 16px",
              backgroundColor: "#FFFFFF",
            }}
          >
            {/* First line: Glossary text or Formula */}
            {valueSourceText && (
              <>
                {fieldType === "Formula" ? (
                  renderFormula(valueSourceText)
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(0, 0, 0, 0.87)",
                      fontSize: "14px",
                      lineHeight: "20px",
                      marginBottom: impactText ? "4px" : 0,
                    }}
                  >
                    {valueSourceText}
                  </Typography>
                )}
              </>
            )}

            {/* Second line: Impact text */}
            {impactText && renderImpactText(impactText)}
          </Box>
        </Box>
      )}

      {/* Existing: Right Section - Copy Icons */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Tooltip
          title={
            buttonStates.monthEnabled
              ? "Copy to next month"
              : "Action unavailable"
          }
          arrow
          placement="top"
        >
          <Box
            onMouseDown={() => {
              if (buttonStates.monthEnabled) {
                handleCopy("month");
              }
            }}
            data-tracker="635"
            sx={{
              padding: "4px",
              borderRadius: "4px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: buttonStates.monthEnabled ? "pointer" : "not-allowed",
              opacity: buttonStates.monthEnabled ? 1 : 0.3,
              transition: "background-color 0.2s, opacity 0.2s",
              "&:hover": {
                backgroundColor: buttonStates.monthEnabled
                  ? "rgba(0, 0, 0, 0.04)"
                  : "transparent",
              },
              "&:active": {
                backgroundColor: buttonStates.monthEnabled
                  ? "rgba(0, 0, 0, 0.08)"
                  : "transparent",
              },
            }}
          >
            <img
              src={CopyToMonthIcon}
              alt="Copy to next month"
              style={{
                width: "32px",
                height: "32px",
                display: "block",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Tooltip>

        <Tooltip
          title={
            buttonStates.quarterEnabled
              ? "Copy to end of quarter"
              : "Action unavailable"
          }
          arrow
          placement="top"
        >
          <Box
            onMouseDown={() => {
              if (buttonStates.quarterEnabled) {
                handleCopy("quarter");
              }
            }}
            data-tracker="636"
            sx={{
              padding: "4px",
              borderRadius: "4px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: buttonStates.quarterEnabled ? "pointer" : "not-allowed",
              opacity: buttonStates.quarterEnabled ? 1 : 0.3,
              transition: "background-color 0.2s, opacity 0.2s",
              "&:hover": {
                backgroundColor: buttonStates.quarterEnabled
                  ? "rgba(0, 0, 0, 0.04)"
                  : "transparent",
              },
              "&:active": {
                backgroundColor: buttonStates.quarterEnabled
                  ? "rgba(0, 0, 0, 0.08)"
                  : "transparent",
              },
            }}
          >
            <img
              src={CopyToQuarterIcon}
              alt="Copy to end of quarter"
              style={{
                width: "32px",
                height: "32px",
                display: "block",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Tooltip>

        <Tooltip
          title={
            buttonStates.yearEnabled
              ? "Copy to end of year"
              : "Action unavailable"
          }
          arrow
          placement="top"
        >
          <Box
            onMouseDown={() => {
              if (buttonStates.yearEnabled) {
                handleCopy("year");
              }
            }}
            data-tracker="637"
            sx={{
              padding: "4px",
              borderRadius: "4px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: buttonStates.yearEnabled ? "pointer" : "not-allowed",
              opacity: buttonStates.yearEnabled ? 1 : 0.3,
              transition: "background-color 0.2s, opacity 0.2s",
              "&:hover": {
                backgroundColor: buttonStates.yearEnabled
                  ? "rgba(0, 0, 0, 0.04)"
                  : "transparent",
              },
              "&:active": {
                backgroundColor: buttonStates.yearEnabled
                  ? "rgba(0, 0, 0, 0.08)"
                  : "transparent",
              },
            }}
          >
            <img
              src={CopyToYearIcon}
              alt="Copy to end of year"
              style={{
                width: "32px",
                height: "32px",
                display: "block",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default InfoCard;
